'use client';

import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

interface ResponsiveContextType {
    isMaxScreen: boolean;
    isScreenXxl: boolean;
    isScreenXl: boolean;
    isScreenLg: boolean;
    isScreenMd: boolean;
    isScreenSm: boolean;
    isScreenXsSm: boolean;
    isScreenXs: boolean;
    isCustomScreenXxxl: boolean;
    isCustomScreenXxl: boolean;
    responsiveWidth: string; // 新增 width 属性
    fullScreenWidth: number;
}

const ResponsiveContext = createContext<ResponsiveContextType | undefined>(undefined);

interface ResponsiveProviderProps {
    children: ReactNode;
}

export const ResponsiveProvider: React.FC<ResponsiveProviderProps> = ({ children }) => {
    // Do not read from window during initialization to avoid SSR crashes,
    // and to ensure the first render remains consistent with SSR
    const [currentWidth, setCurrentWidth] = useState<number>(0);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => setCurrentWidth(window.innerWidth);
        handleResize(); // 掛載後同步一次
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const breakpointState = useMemo(() => {
        const width = currentWidth;

        const isMaxScreen = width >= 1919;
        const isCustomScreenXxxl = width >= 1800 && width < 1900;
        const isCustomScreenXxl = width >= 1440 && width < 1800;
        const isScreenXxl = width >= 1440;
        const isScreenXl = width >= 1200 && width < 1440;
        const isScreenLg = width >= 992 && width < 1200;
        const isScreenMd = width >= 768 && width < 992;
        const isScreenSm = width >= 476 && width < 768;
        const isScreenXsSm = width <= 600;
        const isScreenXs = width <= 475;

        const responsiveWidth = (() => {
            if (isMaxScreen) return '1185px';
            if (isScreenXxl) return '1185px';
            if (isScreenXl) return '1125px';
            if (isScreenLg) return '885px';
            if (isScreenMd) return '650px';
            if (isScreenSm) return '422px';
            return '320px';
        })();

        return {
            isMaxScreen,
            isCustomScreenXxxl,
            isCustomScreenXxl,
            isScreenXxl,
            isScreenXl,
            isScreenLg,
            isScreenMd,
            isScreenSm,
            isScreenXsSm,
            isScreenXs,
            responsiveWidth,
        };
    }, [currentWidth]);

    return (
        <ResponsiveContext.Provider
            value={{
                isMaxScreen: breakpointState.isMaxScreen,
                isScreenXxl: breakpointState.isScreenXxl,
                isScreenXl: breakpointState.isScreenXl,
                isScreenLg: breakpointState.isScreenLg,
                isScreenMd: breakpointState.isScreenMd,
                isScreenSm: breakpointState.isScreenSm,
                isScreenXs: breakpointState.isScreenXs,
                isScreenXsSm: breakpointState.isScreenXsSm,
                responsiveWidth: breakpointState.responsiveWidth,
                fullScreenWidth: currentWidth,
                isCustomScreenXxxl: breakpointState.isCustomScreenXxxl,
                isCustomScreenXxl: breakpointState.isCustomScreenXxl,
            }}
        >
            {children}
        </ResponsiveContext.Provider>
    );
};

export const useResponsive = (): ResponsiveContextType => {
    const context = useContext(ResponsiveContext);
    if (!context) {
        throw new Error('useResponsive must be used within a ResponsiveProvider');
    }
    return context;
};
